/**
 * The formatters of the practitioner's pathway screens.
 *
 * TWO KINDS OF DATE CROSS THIS WIRE (contract, preamble), and they must not share a
 * formatter: CALENDAR DATES ("YYYY-MM-DD") are printed as that calendar day in UTC, so
 * they read the same day in every zone; INSTANTS (ISO-8601 with an offset) are printed in
 * the reader's zone.
 *
 * Absent or unreadable input returns "" — callers render a dash rather than "Invalid Date".
 */
import { dateLocale } from "../../i18n/dateLocale";

const DAY_MS = 86400000;

/** An ISO instant as the reader's local date ("Sep 28, 2026"); "" when absent. */
export const fmtInstantDate = (iso, lang) => {
  if (!iso) return "";
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? new Date(ms).toLocaleDateString(dateLocale(lang), { dateStyle: "medium" }) : "";
};

/**
 * How long ago an instant was, in whole days: "today", "yesterday", "16 days ago"
 * (`Intl.RelativeTimeFormat`, so the words are the reader's language). "" when absent.
 *
 * @param {string|null} iso
 * @param {string} lang       product language
 * @param {number} [now]      ms since the epoch (tests pin it)
 */
export const fmtDaysAgo = (iso, lang, now = Date.now()) => {
  if (!iso) return "";
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return "";
  const days = Math.max(0, Math.floor((now - ms) / DAY_MS));
  return new Intl.RelativeTimeFormat(dateLocale(lang), { numeric: "auto" }).format(-days, "day");
};

/**
 * A byte count as "482 KB" / "1.2 MB" (the unit words are the locale's: "Ko" in French);
 * "" when unknown.
 *
 * @param {number} bytes
 * @param {Function} t  `useT("admin")`; reads `path.size.kb` / `path.size.mb`
 */
export const fmtFileSize = (bytes, t) => {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n <= 0) return "";
  if (n < 1024 * 1024) return t("path.size.kb", { n: Math.max(1, Math.round(n / 1024)) });
  return t("path.size.mb", { n: (n / (1024 * 1024)).toFixed(1) });
};

// ── live-session instants (contract §10.1 `live.starts_at`, §10.11) ──────────

const pad2 = (n) => String(n).padStart(2, "0");

/**
 * A stored UTC instant as the value of an `<input type="datetime-local">`
 * ("2026-11-12T18:00"), in the BROWSER's zone: the practitioner types the session time
 * where they are. "" when absent or unreadable.
 */
export const isoToLocalInput = (iso) => {
  if (!iso) return "";
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return "";
  const d = new Date(ms);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};

/**
 * The value of a `datetime-local` input, read in the browser's zone, as the UTC instant
 * the server stores ("2026-11-12T17:00:00Z"). "" for an empty or partial value (the
 * input reports "" until every part is typed).
 */
export const localInputToIso = (value) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(String(value || ""));
  if (!m) return "";
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5]));
  return Number.isFinite(d.getTime()) ? d.toISOString().replace(/\.\d{3}Z$/, "Z") : "";
};

/** The canvas row's short session label, "Thu 12 Nov · 18:00", in the reader's zone; "" when absent. */
export const fmtSessionShort = (iso, lang) => {
  if (!iso) return "";
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return "";
  const d = new Date(ms);
  const locale = dateLocale(lang);
  const day = d.toLocaleDateString(locale, { weekday: "short", day: "numeric", month: "short" });
  const time = d.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" });
  return `${day} · ${time}`;
};

/** The browser's IANA zone ("Europe/Paris"), named next to a time the practitioner types; "" when unknown. */
export const browserTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  } catch {
    return "";
  }
};
